import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

export type DsCardVariant = 'default' | 'elevated' | 'outline' | 'brand' | 'inverse';
export type DsCardDensity = 'compact' | 'comfortable';

@Component({
  selector: 'ds-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (href()) {
      <a [href]="href()" [class]="classes()" class="ds-card">
        <ng-content />
      </a>
    } @else {
      <article [class]="classes()" class="ds-card">
        <ng-content />
      </article>
    }
  `,
  styles: [`
    :host {
      display: contents;
    }

    .ds-card {
      display: flex;
      flex-direction: column;
      gap: 12px;
      border-radius: var(--r-sm);
      font-family: var(--f-body);
      text-decoration: none;
      position: relative;
      transition:
        transform var(--d-3) var(--e-emph),
        box-shadow var(--d-3) var(--e-emph),
        border-color var(--d-2) var(--e-standard);
    }

    /* ── Density ─────────────────────────────────────────────── */
    .ds-card--compact {
      padding: 18px;
      gap: 8px;
    }

    .ds-card--comfortable {
      padding: 28px;
      gap: 12px;
    }

    /* ── Variants ────────────────────────────────────────────── */
    .ds-card--default {
      background: var(--surface);
      color: var(--on-surface);
      border: 1px solid var(--border-subtle);
      box-shadow: none;
    }

    .ds-card--elevated {
      background: var(--surface);
      color: var(--on-surface);
      border: 1px solid rgba(0, 0, 0, 0.04);
      box-shadow: var(--sh-2);
    }

    .ds-card--outline {
      background: transparent;
      color: var(--on-surface);
      border: 1.5px solid var(--on-surface);
      box-shadow: none;
    }

    /* #400000 = brand DS BearingPoint — pas de token disponible */
    .ds-card--brand {
      background: #400000;
      color: #ffffff;
      border: 1px solid #400000;
      box-shadow: var(--sh-product);
    }

    .ds-card--inverse {
      background: var(--surface-inverse);
      color: var(--on-inverse);
      border: 1px solid var(--surface-inverse);
      box-shadow: none;
    }

    /* ── Interactive ─────────────────────────────────────────── */
    .ds-card--interactive {
      cursor: pointer;

      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--sh-4);
      }

      &:focus-visible {
        outline: 2px solid var(--c-focus);
        outline-offset: 3px;
      }

      &.ds-card--brand:hover {
        box-shadow: 0 18px 40px rgba(255, 47, 63, 0.18);
      }

      &.ds-card--inverse:hover {
        background: #1a1a1a;
      }
    }
  `],
})
export class DsCardComponent {
  readonly variant = input<DsCardVariant>('default');
  readonly density = input<DsCardDensity>('comfortable');
  readonly interactive = input<boolean>(false);
  readonly href = input<string | undefined>(undefined);

  readonly classes = computed(() => {
    const parts = [
      `ds-card--${this.variant()}`,
      `ds-card--${this.density()}`,
    ];
    if (this.interactive() || this.href()) parts.push('ds-card--interactive');
    return parts.join(' ');
  });
}
